import { AlertTriangle, AudioLines, Fingerprint, Gauge } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  evidenceRows,
  evidenceWarnings,
  finalVerdictLabel,
  finalVerdictOf,
  formatPercent,
  formatPermille,
  formatSpeechSeconds,
  parseVoiceFidelity,
  policyModeLabel,
  receiptStatusLabel,
  reportedStatusLabel,
  requirementLabel,
  shortHash,
  stageLabel,
  statusTone,
  type FidelityReceipt,
  type VerdictTone,
} from "./voiceFidelity";

const TONE_CLASSES: Record<VerdictTone, string> = {
  positive: "border-emerald-300 text-emerald-700 dark:text-emerald-400",
  negative: "border-red-300 text-red-700 dark:text-red-400",
  caution: "border-amber-300 text-amber-700 dark:text-amber-400",
};

/**
 * "Voice & accent evidence" — the operator audit view of VAF acoustic
 * receipts for one Echo: what was requested, which voice was intended and
 * used, what an automated evaluator reported at each stage, and the final
 * encoded-audio verdict.
 *
 * It renders only what the indexer projected and never upgrades it: no
 * encoded receipt means "unverified", whatever earlier stages reported, and an
 * evaluator "accepted" is never presented as human listening or delivery.
 * Renders nothing for Echoes without accent-fidelity evidence.
 */
export function EchoVoiceFidelity({ voiceRender }: { voiceRender: unknown }) {
  const evidence = useMemo(
    () => parseVoiceFidelity(voiceRender),
    [voiceRender],
  );
  if (!evidence) return null;

  const verdict = finalVerdictOf(evidence);
  const rows = evidenceRows(evidence);
  const warnings = evidenceWarnings(evidence);
  const verdictTone = statusTone(
    verdict.kind === "unverified" ? "unverified" : verdict.status,
  );

  return (
    <Card data-testid="echo-voice-fidelity">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <AudioLines className="size-4" aria-hidden="true" />
            Voice &amp; accent evidence
          </CardTitle>
          <Badge
            variant="outline"
            className="border-amber-300 font-normal text-amber-700 dark:text-amber-400"
          >
            Automated evaluator · not human listening
          </Badge>
        </div>
        <CardDescription>
          What was requested, which voice narrated it, and what an automated
          acoustic evaluator reported at each stage. Evaluator results are not
          human listening and say nothing about delivery.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <Fact label="Requested accent">
            {evidence.requirement ? (
              requirementLabel(evidence.requirement)
            ) : (
              <span className="text-muted-foreground">
                No accent requirement was projected for this Echo.
              </span>
            )}
          </Fact>
          <Fact label="Fidelity policy mode">
            {policyModeLabel(evidence.policyMode)}
          </Fact>
          <Fact label="Intended (planned) voice">
            <VoiceId value={evidence.plannedVoiceId} />
            {evidence.plannedVoiceType ? (
              <span className="ml-2 text-xs text-muted-foreground">
                {evidence.plannedVoiceType}
              </span>
            ) : null}
          </Fact>
          <Fact label="Effective voice (used for narration)">
            <VoiceId value={evidence.effectiveVoiceId} />
          </Fact>
        </div>

        <div
          className={cn(
            "space-y-2 rounded-md border p-3",
            TONE_CLASSES[verdictTone],
          )}
          data-testid="voice-fidelity-final-verdict"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Gauge className="size-4" aria-hidden="true" />
            <span className="text-xs uppercase tracking-wide">
              Final audio verdict
            </span>
          </div>
          <div className="text-sm font-medium">
            {finalVerdictLabel(verdict)}
          </div>
          {verdict.kind === "evaluated" ? (
            <p className="text-xs text-muted-foreground">
              From the encoded-audio receipt ·{" "}
              {verdict.receipt.evaluatorVersion}
              {" · audio "}
              <code className="font-mono">
                {shortHash(verdict.receipt.audioSha256)}
              </code>
              {evidence.policyMode === "observe"
                ? " · observe mode: recorded only, it did not gate the narration"
                : ""}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              No encoded-audio receipt was reported, so earlier preview,
              saved-voice or take results are not a verdict on the final audio.
            </p>
          )}
          {evidence.reportedStatus ? (
            <p className="text-xs text-muted-foreground">
              Pipeline-reported status:{" "}
              {reportedStatusLabel(evidence.reportedStatus)} (informational; the
              verdict above comes only from the encoded receipt)
            </p>
          ) : null}
        </div>

        {warnings.length > 0 ? (
          <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-400">
            {warnings.map((warning) => (
              <li key={warning} className="flex items-start gap-1.5">
                <AlertTriangle
                  className="mt-0.5 size-3.5 shrink-0"
                  aria-hidden="true"
                />
                {warning}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Evidence by stage
          </div>
          {rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Evaluator class</TableHead>
                  <TableHead className="text-right">Confidence</TableHead>
                  <TableHead className="text-right">Speech</TableHead>
                  <TableHead className="text-right">Windows</TableHead>
                  <TableHead className="text-right">Weakest window</TableHead>
                  <TableHead>Evaluator</TableHead>
                  <TableHead>Audio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <ReceiptRow
                    key={`${row.stage}-${row.audioSha256}-${index}`}
                    row={row}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              No acoustic receipts were reported for this Echo.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Receipts identify audio by SHA-256 only; they carry no file
            reference, so there is no per-receipt playback. Listen to the
            delivered film in the player above.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ReceiptRow({ row }: { row: FidelityReceipt }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        {stageLabel(row.stage)}
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={cn("font-normal", TONE_CLASSES[statusTone(row.status)])}
        >
          {receiptStatusLabel(row.status)}
        </Badge>
      </TableCell>
      <TableCell>
        {row.observedAccent ? (
          <code className="font-mono text-xs">{row.observedAccent}</code>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatPercent(row.confidence)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatSpeechSeconds(row.speechMs)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {row.windows ?? "—"}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatPermille(row.minWindowPermille)}
      </TableCell>
      <TableCell className="text-xs">{row.evaluatorVersion}</TableCell>
      <TableCell>
        <code
          className="font-mono text-xs"
          title={`audio ${row.audioSha256} · voice ${row.voiceId} · model ${row.modelId}`}
        >
          {shortHash(row.audioSha256)}
        </code>
      </TableCell>
    </TableRow>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

function VoiceId({ value }: { value: string | null }) {
  if (!value) {
    return <span className="text-muted-foreground">Not reported</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <Fingerprint
        className="size-3.5 text-muted-foreground"
        aria-hidden="true"
      />
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
        {value}
      </code>
    </span>
  );
}
